import React, { useState } from 'react';
import { Edit2, Trash2, X } from 'lucide-react';

const AddExerciseModal = ({ exercises, onAdd, onUpdate, onDelete, onClose }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('strength');
  const [avgSpeedMetric, setAvgSpeedMetric] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const exerciseData = {
      name,
      type,
      avgSpeedMetric: type === 'cardio' ? avgSpeedMetric : false
    };

    if (editingId) {
      await onUpdate(editingId, exerciseData);
      setEditingId(null);
    } else {
      await onAdd(exerciseData);
    }
    
    setName('');
    setType('strength');
    setAvgSpeedMetric(false);
  };

  const startEdit = (exercise) => {
    setName(exercise.name);
    setType(exercise.type);
    setAvgSpeedMetric(exercise.avgSpeedMetric || false);
    setEditingId(exercise.id);
  };

  const cancelEdit = () => {
    setName('');
    setType('strength');
    setAvgSpeedMetric(false);
    setEditingId(null);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Manage Exercise Types</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mb-6 pb-6 border-b border-slate-700">
          <h3 className="text-lg font-bold text-slate-300">
            {editingId ? 'Edit Exercise Type' : 'Add New Exercise Type'}
          </h3>
          
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-300">Exercise Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Cycling, Swimming, Pull-ups"
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-300">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="strength">Strength Training</option>
              <option value="cardio">Cardio</option>
            </select>
          </div>

          {type === 'cardio' && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="avgSpeed"
                checked={avgSpeedMetric}
                onChange={(e) => setAvgSpeedMetric(e.target.checked)}
                className="w-5 h-5 rounded border-slate-600 bg-slate-700 focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="avgSpeed" className="text-sm text-slate-300">
                Track average speed for this exercise
              </label>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all shadow-lg shadow-purple-500/50"
            >
              {editingId ? 'Update Exercise' : 'Add Exercise Type'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={cancelEdit}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-semibold transition-all"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <div>
          <h3 className="text-lg font-bold mb-4 text-slate-300">Current Exercise Types</h3>
          <div className="space-y-2">
            {exercises.map(exercise => (
              <div key={exercise.id} className="flex justify-between items-center bg-slate-700/50 p-4 rounded-lg">
                <div>
                  <div className="font-semibold">{exercise.name}</div>
                  <div className="text-sm text-slate-400">
                    {exercise.type === 'strength' ? 'Strength Training' : 'Cardio'}
                    {exercise.avgSpeedMetric && ' • Tracks avg speed'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(exercise)}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => onDelete(exercise.id)}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddExerciseModal;
